"use client";

import { useState, useEffect } from "react";
import { apiConfig } from "../config/api.config";
import CDPViewer from "./CDPViewer";
import RPViewer from "./RPViewer";

export default function PresupuestoSolicitudesModal({
  show,
  onClose,
  contrato,
  userRole,
}) {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showFormularioViewer, setShowFormularioViewer] = useState(false);
  const [currentFormularioData, setCurrentFormularioData] = useState(null);
  const [currentFormularioType, setCurrentFormularioType] = useState(null);
  const [currentSolicitud, setCurrentSolicitud] = useState(null);

  const [responseForms, setResponseForms] = useState({});
  const [savingResponseId, setSavingResponseId] = useState(null);

  const contractId = contrato?.id;
  const contractData = contrato;

  useEffect(() => {
    if (!show) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, contractId, contrato?.es_otras_cdp_rp]);

  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string" && value.trim() === "") return true;
    if (Array.isArray(value) && value.length === 0) return true;

    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      return true;
    }

    return false;
  };

  const formatFieldLabel = (key) => {
    return String(key)
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const extractMotivoRechazo = (req) => {
    if (!req) return null;

    const candidates = [
      req.motivoRechazo,
      req.motivo_rechazo,
      req.motivo,
      req.observaciones,
      req.observacion,
      req.notes,
      req.note,
      req.razon,
      req.razon_rechazo,
      req.rejection_reason,
      req.rejection_note,
      req.comentario,
      req.comentarios,
      req.respuesta,
      req.response_note,
      req.response_notes,
      req.response_message,
      req.mensaje,
      req.descripcion,
    ];

    for (const c of candidates) {
      if (c && String(c).trim() !== "") return String(c).trim();
    }

    return null;
  };

  const extractNumeroDocumento = (req) => {
    return (
      req?.numero_documento ||
      req?.numero_cdp ||
      req?.numero_rp ||
      req?.num_documento ||
      req?.doc_number ||
      req?.document_number ||
      null
    );
  };

  const extractFechaDocumento = (req) => {
    return (
      req?.fecha_documento ||
      req?.fecha_respuesta ||
      req?.response_date ||
      req?.responded_at ||
      null
    );
  };

  const extractNuevoResumido = (req) => {
    return (
      req?.nuevo_resumido ||
      req?.resumido ||
      req?.valor_aprobado ||
      req?.monto_aprobado ||
      null
    );
  };

  const resolveFileUrl = (rawUrl) => {
    if (!rawUrl) return "";

    let u = String(rawUrl).trim();
    if (!u) return "";

    if (/^https?:\/\//i.test(u)) {
      if (u.includes("/uploads/") && !u.includes("/api/uploads/")) {
        u = u.replace("/uploads/", "/api/uploads/");
      }

      return u;
    }

    if (!u.startsWith("/")) u = "/" + u;

    if (u.startsWith("/uploads/")) {
      u = "/api" + u;
    }

    if (u.startsWith("/api/")) {
      return apiConfig.getUrl(u);
    }

    return apiConfig.getUrl(u);
  };

  const handleDownload = async (url, name) => {
    try {
      const downloadUrl = resolveFileUrl(url);

      if (!downloadUrl) {
        throw new Error("URL inválida para descarga.");
      }

      const response = await fetch(downloadUrl, {
        credentials: "include",
        redirect: "follow",
        headers: {
          Accept: "application/pdf,application/octet-stream;q=0.9,*/*;q=0.8",
        },
      });

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status}. ${txt.slice(0, 180)}`);
      }

      const contentType = (
        response.headers.get("content-type") || ""
      ).toLowerCase();

      if (contentType.includes("text/html")) {
        const txt = await response.text().catch(() => "");
        throw new Error(
          `El servidor devolvió HTML. Muestra: ${txt.slice(0, 140)}`
        );
      }

      if (contentType.includes("application/json")) {
        const txt = await response.text().catch(() => "");
        throw new Error(
          `El servidor devolvió JSON. Muestra: ${txt.slice(0, 180)}`
        );
      }

      const blob = await response.blob();

      if (!blob || blob.size < 50) {
        throw new Error("Archivo vacío o demasiado pequeño.");
      }

      const head = await blob.slice(0, 5).text().catch(() => "");
      const looksLikePdf = head === "%PDF-";

      if (!looksLikePdf) {
        const peek = await blob.slice(0, 120).text().catch(() => "");
        throw new Error(
          `Lo descargado no parece PDF. Firma: "${head}". Muestra: ${peek.slice(
            0,
            120
          )}`
        );
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;

      let safeName = (name && String(name).trim()) || "archivo.pdf";
      if (!safeName.toLowerCase().endsWith(".pdf")) safeName += ".pdf";

      link.download = safeName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("❌ Error al descargar:", err);
      alert(`Error al descargar.\n\n${err?.message || "Desconocido"}`);
    }
  };

  const parseFormData = (value) => {
    try {
      if (!value) return {};
      return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
      return {};
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    if (contrato?.es_otras_cdp_rp) {
      try {
        const urlPres = apiConfig.getUrl(
          "/api/presupuesto/solicitudes/index.php",
          `contract_id=${encodeURIComponent(contractId)}`
        );

        const resPres = await fetch(urlPres, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (!resPres.ok) {
          throw new Error(`HTTP ${resPres.status}`);
        }

        const json = await resPres.json();
        const rows = json.solicitudes || json.data || json || [];
        const arr = Array.isArray(rows) ? rows : [rows];

        const solicitudesOtras = arr
          .filter(
            (r) =>
              String(r.source_module || "")
                .toLowerCase()
                .trim() === "otras_cdp_rp"
          )
          .map((r) => {
            const tipo = String(r.request_type || "").toUpperCase();

            return {
              ...r,
              request_type: tipo,
              formulario: parseFormData(
                r.form_data || r.formData || r.formulario || r.data || {}
              ),
              sent_at: r.sent_at || r.created_at || contrato?.created_at || null,
            };
          });

        setSolicitudes(solicitudesOtras);
        return;
      } catch (err) {
        console.error("❌ Error cargando otras necesidades CDP/RP:", err);
        setError("Error al cargar solicitudes de otras necesidades CDP/RP");
        return;
      } finally {
        setLoading(false);
      }
    }

    try {
      const urlPres = apiConfig.getUrl(
        "/api/presupuesto/solicitudes/index.php",
        `contract_id=${encodeURIComponent(contractId)}`
      );

      const resPres = await fetch(urlPres, { credentials: "include" });
      if (!resPres.ok) throw new Error(`HTTP ${resPres.status}`);

      const json = await resPres.json();
      const presRequests = json.solicitudes || json.data || json || [];

      const urlCdp = apiConfig.getUrl(
        "/api/cdp/formularios/index.php",
        `contract_id=${encodeURIComponent(contractId)}`
      );

      const urlRp = apiConfig.getUrl(
        "/api/rp/formularios/index.php",
        `contract_id=${encodeURIComponent(contractId)}`
      );

      const [resCdp, resRp] = await Promise.all([
        fetch(urlCdp, { credentials: "include" }).catch(() => null),
        fetch(urlRp, { credentials: "include" }).catch(() => null),
      ]);

      let cdpForms = [];
      let rpForms = [];

      if (resCdp?.ok) {
        const dataCdp = await resCdp.json();
        cdpForms = Array.isArray(dataCdp)
          ? dataCdp
          : dataCdp.formularios || dataCdp.data || dataCdp.rows || [];
      }

      if (resRp?.ok) {
        const dataRp = await resRp.json();
        rpForms = Array.isArray(dataRp)
          ? dataRp
          : dataRp.formularios || dataRp.data || dataRp.rows || [];
      }

      const solicitudesConFormularios = presRequests.map((r) => {
        const tipo = String(r.request_type || "").toUpperCase();
        let formulario = null;

        if (tipo === "CDP" && cdpForms.length > 0) formulario = cdpForms[0];
        else if (tipo === "RP" && rpForms.length > 0) formulario = rpForms[0];

        return { ...r, request_type: tipo, formulario };
      });

      let solicitudesAlmacen = [];

      try {
        const almacenEndpoint =
          apiConfig?.endpoints?.almacenSolicitudes ||
          "/api/almacen/solicitudes/index.php";

        const urlAlm = apiConfig.getUrl(
          almacenEndpoint,
          `contract_id=${encodeURIComponent(contractId)}`
        );

        const resAlm = await fetch(urlAlm, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (resAlm?.ok) {
          const jsonAlm = await resAlm.json();

          const arr = Array.isArray(jsonAlm)
            ? jsonAlm
            : jsonAlm?.data || jsonAlm?.rows || [];

          solicitudesAlmacen = (Array.isArray(arr) ? arr : []).map((r) => ({
            ...r,
            request_type: "ALMACÉN",
            sent_at: r.sent_at || r.created_at || r.fecha || null,
          }));
        }
      } catch (err) {
        console.warn("No se pudieron cargar solicitudes de almacén:", err);
      }

      let solicitudesInexistencia = [];

      try {
        const urlInex = apiConfig.getUrl(
          "/api/inexistencia/by-contract/index.php",
          `contract_id=${encodeURIComponent(contractId)}`
        );

        const resInex = await fetch(urlInex, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (resInex?.ok) {
          const jsonInex = await resInex.json();

          const arr = Array.isArray(jsonInex)
            ? jsonInex
            : jsonInex?.rows || jsonInex?.data || [];

          solicitudesInexistencia = (Array.isArray(arr) ? arr : []).map((r) => {
            const rawStatus = String(r.status || "").toLowerCase();

            let response_status = "pending";

            if (
              rawStatus === "approved" ||
              rawStatus === "finalizado" ||
              rawStatus === "aprobado"
            ) {
              response_status = "aprobado";
            } else if (
              rawStatus === "rechazado" ||
              rawStatus === "rejected" ||
              rawStatus === "negado"
            ) {
              response_status = "negado";
            }

            return {
              ...r,
              request_type: "INEXISTENCIA",
              sent_at: r.sent_at || r.created_at || null,
              response_status,
              motivo_rechazo: r.notes || null,
              pdf_url: r.pdf_url || null,
              objeto_snapshot: r.objeto_snapshot || null,
            };
          });
        }
      } catch (err) {
        console.warn("No se pudieron cargar solicitudes de inexistencia:", err);
      }

      const todasSolicitudes = [
        ...solicitudesConFormularios,
        ...solicitudesAlmacen,
        ...solicitudesInexistencia,
      ];

      todasSolicitudes.sort((a, b) => {
        const dateA = new Date(a.sent_at || 0).getTime();
        const dateB = new Date(b.sent_at || 0).getTime();
        return dateB - dateA;
      });

      setSolicitudes(todasSolicitudes);
    } catch (err) {
      console.error("❌ Error:", err);
      setError("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  };

  // CAMBIO 1: toggleFormularioRespuesta ahora recibe accion ("aprobar"|"negar")
  const toggleFormularioRespuesta = (req, accion = "aprobar") => {
    setResponseForms((prev) => ({
      ...prev,
      [req.id]: prev[req.id]
        ? null
        : {
            accion,
            numero_documento: "",
            fecha_documento: new Date().toISOString().slice(0, 10),
            nuevo_resumido: "",
            motivo_rechazo: "",
            archivo_respuesta: null,
          },
    }));
  };

  const handleRespuestaChange = (requestId, field, value) => {
    setResponseForms((prev) => ({
      ...prev,
      [requestId]: {
        ...(prev[requestId] || {}),
        [field]: value,
      },
    }));
  };

  // CAMBIO 2: guardarRespuestaSolicitud maneja aprobar/negar + archivo
  const guardarRespuestaSolicitud = async (req) => {
    try {
      const form = responseForms[req.id] || {};
      const esNegar = form.accion === "negar";

      if (!esNegar && !form.numero_documento?.trim()) {
        alert(`Debes ingresar el número de ${req.request_type}.`);
        return;
      }

      if (!esNegar && !form.fecha_documento?.trim()) {
        alert(`Debes ingresar la fecha de ${req.request_type}.`);
        return;
      }

      if (esNegar && !form.motivo_rechazo?.trim()) {
        alert("Debes ingresar el motivo del rechazo.");
        return;
      }

      setSavingResponseId(req.id);

      // Subir archivo de respuesta si existe
      let responseAttachments = [];
      if (!esNegar && form.archivo_respuesta instanceof File) {
        const fd = new FormData();
        fd.append("file", form.archivo_respuesta, form.archivo_respuesta.name);
        fd.append("fileName", form.archivo_respuesta.name);
        fd.append("requestId", String(req.id));

        // Endpoint real de subida de archivos
        const uploadRes = await fetch(
          apiConfig.getUrl("/api/presupuesto/solicitudes/upload/index.php"),
          { method: "POST", credentials: "include", body: fd }
        );

        if (uploadRes.ok) {
          const uploadData = await uploadRes.json().catch(() => ({}));
          // Backend devuelve { ok, url, fileName }
          if (uploadData?.ok && uploadData?.url) {
            responseAttachments = [{
              url:  uploadData.url,
              name: uploadData.fileName || form.archivo_respuesta.name,
            }];
          } else {
            console.warn("⚠️ Upload respondió pero sin url:", uploadData);
          }
        } else {
          const errTxt = await uploadRes.text().catch(() => "");
          console.warn("⚠️ No se pudo subir el archivo de respuesta:", uploadRes.status, errTxt);
        }
      }

      const body = esNegar
        ? {
            requestId: req.id,
            status: "negado",
            response_status: "negado",
            motivo_rechazo: form.motivo_rechazo,
            responded_at: new Date().toISOString().slice(0, 19).replace("T", " "),
          }
        : {
            requestId: req.id,
            status: "completed",
            response_status: "completed",
            numero_documento: form.numero_documento,
            fecha_documento: form.fecha_documento,
            nuevo_resumido: form.nuevo_resumido || "",
            response_attachments: responseAttachments,
            responded_at: new Date().toISOString().slice(0, 19).replace("T", " "),
          };

      const response = await fetch(
        apiConfig.getUrl("/api/presupuesto/solicitudes/index.php"),
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      if (!response.ok || data?.ok === false) {
        throw new Error(data?.error || "No se pudo guardar la respuesta.");
      }

      alert(`${req.request_type} ${esNegar ? "negado" : "aprobado"} correctamente.`);

      setSolicitudes((prev) => prev.filter((item) => item.id !== req.id));

      setResponseForms((prev) => {
        const copy = { ...prev };
        delete copy[req.id];
        return copy;
      });

      await loadData();
    } catch (error) {
      console.error("❌ Error guardando respuesta:", error);
      alert(error.message || "Error guardando respuesta.");
    } finally {
      setSavingResponseId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const d = new Date(dateString);
      return d.toLocaleDateString("es-ES");
    } catch {
      return "N/A";
    }
  };

  const extractAttachments = (attachmentsData) => {
    if (!attachmentsData) return [];

    try {
      const parsed =
        typeof attachmentsData === "string"
          ? JSON.parse(attachmentsData)
          : attachmentsData;

      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "object" && parsed) return Object.values(parsed);
    } catch (e) {
      console.error("Error parsing attachments:", e);
    }

    return [];
  };

  const getTipoBadge = (tipo) => {
    switch (tipo) {
      case "ANULAR_CDP":
        return { bg: "#fee2e2", color: "#991b1b", label: "ANULAR CDP" };
      case "CDP":
        return { bg: "#fef9c3", color: "#854d0e", label: "CDP" };
      case "RP":
        return { bg: "#dcfce7", color: "#166534", label: "RP" };
      case "ARL":
        return { bg: "#ffedd5", color: "#9a3412", label: "ARL" };
      case "ALMACÉN":
        return { bg: "#dbeafe", color: "#1e40af", label: "ALMACÉN" };
      case "INEXISTENCIA":
        return { bg: "#ede9fe", color: "#5b21b6", label: "INEXISTENCIA" };
      default:
        return { bg: "#f3f4f6", color: "#374151", label: tipo };
    }
  };

  const getCardColors = (tipo) => {
    switch (tipo) {
      case "ANULAR_CDP":
        return { bg: "#fffafa", border: "#fca5a5" };
      case "ARL":
        return { bg: "#fffbf7", border: "#fdba74" };
      case "ALMACÉN":
        return { bg: "#f8faff", border: "#bfdbfe" };
      case "INEXISTENCIA":
        return { bg: "#faf8ff", border: "#ddd6fe" };
      case "CDP":
        return { bg: "#fffdf0", border: "#fde68a" };
      case "RP":
        return { bg: "#f6fef9", border: "#bbf7d0" };
      default:
        return { bg: "#fafafa", border: "#e5e7eb" };
    }
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          width: "95%",
          maxWidth: "700px",
          maxHeight: "80vh",
          borderRadius: "0.5rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            backgroundColor: "#0e7490",
            color: "white",
            padding: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>
              Solicitudes — {contractData?.nombre_contratista}
            </h3>

            <button
              onClick={loadData}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.3)",
                color: "white",
                padding: "0.25rem 0.5rem",
                borderRadius: "0.25rem",
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            >
              🔄
            </button>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "1.5rem",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "1rem", overflowY: "auto", flex: 1 }}>
          {loading ? (
            <p style={{ textAlign: "center", color: "#666" }}>Cargando...</p>
          ) : error ? (
            <p style={{ textAlign: "center", color: "#dc2626" }}>{error}</p>
          ) : solicitudes.length === 0 ? (
            <p style={{ textAlign: "center", color: "#666" }}>
              No hay solicitudes.
            </p>
          ) : (
            solicitudes.map((req, idx) => {
              const tipo = req.request_type;
              const status = String(
                req.response_status || req.status || "pending"
              ).toLowerCase();

              const isNegado = ["negado", "rechazado", "denegado"].includes(
                status
              );

              const isAprobado = [
                "aprobado",
                "approved",
                "completed",
              ].includes(status);

              const isAlmacen = tipo === "ALMACÉN";
              const isInexistencia = tipo === "INEXISTENCIA";
              const isAnularCdp = tipo === "ANULAR_CDP";
              const isOtrasCDPRP = req.source_module === "otras_cdp_rp";

              const tipoBadge = getTipoBadge(tipo);
              const cardColors = getCardColors(tipo);

              const bgColor = isAprobado
                ? "#dcfce7"
                : isNegado
                ? "#fee2e2"
                : "#fef9c3";

              const textColor = isAprobado
                ? "#166534"
                : isNegado
                ? "#b91c1c"
                : "#854d0e";

              const attachments = extractAttachments(req.attachments);
              const responseAttachments = extractAttachments(
                req.response_attachments
              );

              const motivoRechazo = extractMotivoRechazo(req);
              const numeroDocumento = extractNumeroDocumento(req);
              const fechaDocumento = extractFechaDocumento(req);
              const nuevoResumido = extractNuevoResumido(req);

              const activeForm = responseForms[req.id];
              const esNegar = activeForm?.accion === "negar";

              return (
                <div
                  key={req.id || idx}
                  style={{
                    marginBottom: "1rem",
                    padding: "1rem",
                    border: `2px solid ${cardColors.border}`,
                    borderRadius: "0.5rem",
                    backgroundColor: cardColors.bg,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.75rem",
                    }}
                  >
                    <div>
                      <strong>Solicitud</strong>

                      {tipo && (
                        <span
                          style={{
                            marginLeft: "0.5rem",
                            padding: "0.25rem 0.6rem",
                            borderRadius: "9999px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            backgroundColor: tipoBadge.bg,
                            color: tipoBadge.color,
                            border: `1px solid ${cardColors.border}`,
                          }}
                        >
                          {tipoBadge.label}
                        </span>
                      )}

                      <span
                        style={{
                          marginLeft: "0.5rem",
                          color: "#6b7280",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formatDate(req.sent_at)}
                      </span>

                      <span
                        style={{
                          marginLeft: "0.75rem",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          backgroundColor: bgColor,
                          color: textColor,
                        }}
                      >
                        {isAprobado
                          ? "Respondido"
                          : isNegado
                          ? "Negado"
                          : "Pendiente"}
                      </span>
                    </div>
                  </div>

                  {isAnularCdp && (
                    <div
                      style={{
                        marginBottom: "0.75rem",
                        padding: "0.5rem 0.75rem",
                        backgroundColor: "#FEE2E2",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        color: "#7F1D1D",
                      }}
                    >
                      <strong>Solicitud de anulación de CDP</strong>
                    </div>
                  )}

                  {isInexistencia && req.objeto_snapshot && (
                    <div
                      style={{
                        marginBottom: "0.75rem",
                        fontSize: "0.875rem",
                        color: "#374151",
                      }}
                    >
                      <strong>Objeto:</strong>{" "}
                      <span style={{ fontStyle: "italic" }}>
                        {req.objeto_snapshot}
                      </span>
                    </div>
                  )}

                  {attachments.length > 0 && (
                    <div style={{ marginBottom: "0.75rem" }}>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: "0.5rem",
                        }}
                      >
                        📤 Documentos enviados:
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}
                      >
                        {attachments.map((file, i) => {
                          const url =
                            typeof file === "string"
                              ? file
                              : file?.url || file?.path || file?.file_url;

                          const name =
                            typeof file === "object" && file?.name
                              ? file.name
                              : `Archivo ${i + 1}`;

                          if (!url) return null;

                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => handleDownload(url, name)}
                              style={{
                                padding: "0.5rem 1rem",
                                backgroundColor: "#3b82f6",
                                color: "white",
                                borderRadius: "0.375rem",
                                fontSize: "0.875rem",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              📄 {name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {req.formulario && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentSolicitud(req);
                        setCurrentFormularioData(req.formulario);

                        setCurrentFormularioType(
                          req.source_module === "otras_cdp_rp"
                            ? "OTRAS_CDP_RP"
                            : tipo
                        );

                        setShowFormularioViewer(true);
                      }}
                      style={{
                        backgroundColor: "#0ea5e9",
                        color: "white",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.375rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      Ver Formulario {tipo}
                    </button>
                  )}

                  {isAnularCdp && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentSolicitud(req);
                        setCurrentFormularioData(null);
                        setCurrentFormularioType("ANULAR_CDP");
                        setShowFormularioViewer(true);
                      }}
                      style={{
                        backgroundColor: "#DC2626",
                        color: "white",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.375rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      Ver Detalle Anulación
                    </button>
                  )}

                  {isAlmacen &&
                    (attachments.length > 0 || responseAttachments.length > 0) && (
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentSolicitud(req);
                          setCurrentFormularioData(null);
                          setCurrentFormularioType("ALMACÉN");
                          setShowFormularioViewer(true);
                        }}
                        style={{
                          backgroundColor: "#3B82F6",
                          color: "white",
                          padding: "0.5rem 1rem",
                          borderRadius: "0.375rem",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          marginBottom: "0.75rem",
                        }}
                      >
                        Ver Documentos
                      </button>
                    )}

                  {isInexistencia && isAprobado && req.pdf_url && (
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload(
                          req.pdf_url,
                          `certificado_inexistencia_${req.id || idx}.pdf`
                        )
                      }
                      style={{
                        backgroundColor: "#7C3AED",
                        color: "white",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.375rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        marginBottom: "0.75rem",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.4rem",
                      }}
                    >
                      📄 Descargar Certificado
                    </button>
                  )}

                  {/* CAMBIO 3: bloque respuesta otras_cdp_rp con aprobar/negar + archivo */}
                  {isOtrasCDPRP && !isAprobado && !isNegado && (
                    <div
                      style={{
                        marginTop: "0.75rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      {!activeForm ? (
                        /* Dos botones: Aprobar / Negar */
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => toggleFormularioRespuesta(req, "aprobar")}
                            style={{
                              backgroundColor: "#16a34a",
                              color: "white",
                              border: "none",
                              padding: "0.6rem 1.1rem",
                              borderRadius: "0.5rem",
                              cursor: "pointer",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                            }}
                          >
                            ✅ Aprobar
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleFormularioRespuesta(req, "negar")}
                            style={{
                              backgroundColor: "#dc2626",
                              color: "white",
                              border: "none",
                              padding: "0.6rem 1.1rem",
                              borderRadius: "0.5rem",
                              cursor: "pointer",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                            }}
                          >
                            ❌ Negar
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{
                            marginTop: "0.75rem",
                            padding: "1rem",
                            border: `1px solid ${esNegar ? "#fecaca" : "#99f6e4"}`,
                            borderRadius: "0.75rem",
                            backgroundColor: esNegar ? "#fff5f5" : "#f0fdfa",
                            display: "grid",
                            gap: "0.75rem",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              color: esNegar ? "#b91c1c" : "#0f766e",
                              fontSize: "1rem",
                              fontWeight: 800,
                            }}
                          >
                            {esNegar ? "❌ Negar solicitud" : "✅ Aprobar solicitud"}
                          </h4>

                          {/* Campos solo para aprobar */}
                          {!esNegar && (
                            <>
                              <label style={{ display: "grid", gap: "0.35rem", fontWeight: 700 }}>
                                Número {req.request_type}
                                <input
                                  type="text"
                                  value={activeForm?.numero_documento || ""}
                                  onChange={(e) =>
                                    handleRespuestaChange(req.id, "numero_documento", e.target.value)
                                  }
                                  style={{ padding: "0.6rem", borderRadius: "0.5rem", border: "1px solid #cbd5e1" }}
                                />
                              </label>

                              <label style={{ display: "grid", gap: "0.35rem", fontWeight: 700 }}>
                                Fecha {req.request_type}
                                <input
                                  type="date"
                                  value={activeForm?.fecha_documento || ""}
                                  onChange={(e) =>
                                    handleRespuestaChange(req.id, "fecha_documento", e.target.value)
                                  }
                                  style={{ padding: "0.6rem", borderRadius: "0.5rem", border: "1px solid #cbd5e1" }}
                                />
                              </label>

                              <label style={{ display: "grid", gap: "0.35rem", fontWeight: 700 }}>
                                Observación / resumido
                                <textarea
                                  value={activeForm?.nuevo_resumido || ""}
                                  onChange={(e) =>
                                    handleRespuestaChange(req.id, "nuevo_resumido", e.target.value)
                                  }
                                  rows={3}
                                  style={{ padding: "0.6rem", borderRadius: "0.5rem", border: "1px solid #cbd5e1", resize: "vertical" }}
                                />
                              </label>

                              <label style={{ display: "grid", gap: "0.35rem", fontWeight: 700 }}>
                                Adjuntar documento {req.request_type} (opcional)
                                <input
                                  type="file"
                                  accept="application/pdf,.pdf"
                                  onChange={(e) =>
                                    handleRespuestaChange(req.id, "archivo_respuesta", e.target.files?.[0] || null)
                                  }
                                  style={{ padding: "0.4rem", borderRadius: "0.5rem", border: "1px solid #cbd5e1", backgroundColor: "white", fontSize: "0.85rem" }}
                                />
                                {activeForm?.archivo_respuesta && (
                                  <span style={{ fontSize: "0.78rem", color: "#15803d" }}>
                                    📄 {activeForm.archivo_respuesta.name}
                                  </span>
                                )}
                              </label>
                            </>
                          )}

                          {/* Campo solo para negar */}
                          {esNegar && (
                            <label style={{ display: "grid", gap: "0.35rem", fontWeight: 700 }}>
                              Motivo del rechazo *
                              <textarea
                                value={activeForm?.motivo_rechazo || ""}
                                onChange={(e) =>
                                  handleRespuestaChange(req.id, "motivo_rechazo", e.target.value)
                                }
                                rows={3}
                                placeholder="Describe el motivo del rechazo..."
                                style={{ padding: "0.6rem", borderRadius: "0.5rem", border: "1px solid #fca5a5", resize: "vertical" }}
                              />
                            </label>
                          )}

                          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                            <button
                              type="button"
                              disabled={savingResponseId === req.id}
                              onClick={() => guardarRespuestaSolicitud(req)}
                              style={{
                                backgroundColor: esNegar ? "#dc2626" : "#16a34a",
                                color: "white",
                                border: "none",
                                padding: "0.6rem 1rem",
                                borderRadius: "0.5rem",
                                cursor: "pointer",
                                fontWeight: 700,
                                opacity: savingResponseId === req.id ? 0.7 : 1,
                              }}
                            >
                              {savingResponseId === req.id
                                ? "Guardando..."
                                : esNegar ? "Confirmar rechazo" : "Guardar aprobación"}
                            </button>

                            <button
                              type="button"
                              onClick={() => toggleFormularioRespuesta(req)}
                              style={{
                                backgroundColor: "#e5e7eb",
                                color: "#111827",
                                border: "none",
                                padding: "0.6rem 1rem",
                                borderRadius: "0.5rem",
                                cursor: "pointer",
                                fontWeight: 700,
                              }}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* FIN CAMBIO 3 */}

                  <div
                    style={{
                      marginTop: "0.5rem",
                      padding: "0.75rem 1rem",
                      backgroundColor: isAprobado
                        ? "#f0fdf4"
                        : isNegado
                        ? "#fff5f5"
                        : "#fffbeb",
                      border: `1px solid ${
                        isAprobado
                          ? "#bbf7d0"
                          : isNegado
                          ? "#fecaca"
                          : "#fde68a"
                      }`,
                      borderRadius: "0.5rem",
                      borderLeft: `4px solid ${
                        isAprobado
                          ? "#22c55e"
                          : isNegado
                          ? "#ef4444"
                          : "#f59e0b"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginBottom: "0.4rem",
                      }}
                    >
                      <span style={{ fontSize: "1.1rem" }}>
                        {isAprobado ? "✅" : isNegado ? "❌" : "⏳"}
                      </span>

                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          color: isAprobado
                            ? "#15803d"
                            : isNegado
                            ? "#b91c1c"
                            : "#92400e",
                        }}
                      >
                        {isAprobado
                          ? "Respondido"
                          : isNegado
                          ? "Negado"
                          : "Pendiente"}
                      </span>
                    </div>

                    {(req.responded_at || fechaDocumento) && (
                      <p
                        style={{
                          margin: "0.2rem 0",
                          fontSize: "0.8rem",
                          color: "#6b7280",
                        }}
                      >
                        Respondido:{" "}
                        {formatDate(req.responded_at || fechaDocumento)}
                      </p>
                    )}

                    {isAprobado &&
                      fechaDocumento &&
                      fechaDocumento !== req.responded_at && (
                        <p
                          style={{
                            margin: "0.2rem 0",
                            fontSize: "0.8rem",
                            color: "#6b7280",
                          }}
                        >
                          Fecha documento: {formatDate(fechaDocumento)}
                        </p>
                      )}

                    {isAprobado && numeroDocumento && (
                      <p
                        style={{
                          margin: "0.2rem 0",
                          fontSize: "0.875rem",
                          color: "#15803d",
                          fontWeight: 600,
                        }}
                      >
                        {isAnularCdp ? "CDP anulado N°:" : "N° documento:"}{" "}
                        {numeroDocumento}
                      </p>
                    )}

                    {isAprobado && !isAnularCdp && nuevoResumido && (
                      <div
                        style={{
                          marginTop: "0.5rem",
                          padding: "0.5rem 0.75rem",
                          backgroundColor: "rgba(255,255,255,0.6)",
                          borderRadius: "0.375rem",
                        }}
                      >
                        <strong
                          style={{
                            fontSize: "0.8rem",
                            color: "#374151",
                          }}
                        >
                          Nuevo resumido:
                        </strong>

                        <p
                          style={{
                            margin: "0.2rem 0 0 0",
                            fontSize: "0.8rem",
                            whiteSpace: "pre-wrap",
                            color: "#4b5563",
                          }}
                        >
                          {nuevoResumido}
                        </p>
                      </div>
                    )}

                    {responseAttachments.length > 0 && (
                      <div style={{ marginTop: "0.75rem" }}>
                        <div
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            marginBottom: "0.4rem",
                            color: "#15803d",
                          }}
                        >
                          📥 Documentos de respuesta:
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.4rem",
                          }}
                        >
                          {responseAttachments.map((file, i) => {
                            const url =
                              typeof file === "string" ? file : file?.url;

                            const name =
                              typeof file === "object" && file?.name
                                ? file.name
                                : `Respuesta ${i + 1}`;

                            return url ? (
                              <button
                                key={i}
                                type="button"
                                onClick={() => handleDownload(url, name)}
                                style={{
                                  padding: "0.4rem 0.8rem",
                                  backgroundColor: "#10b981",
                                  color: "white",
                                  borderRadius: "0.375rem",
                                  fontSize: "0.8rem",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                📄 {name}
                              </button>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {isNegado && motivoRechazo && (
                      <div
                        style={{
                          marginTop: "0.5rem",
                          padding: "0.6rem 0.75rem",
                          backgroundColor: "#fff1f1",
                          border: "1px solid #fecaca",
                          borderRadius: "0.375rem",
                        }}
                      >
                        <strong
                          style={{
                            color: "#dc2626",
                            fontSize: "0.8rem",
                          }}
                        >
                          Motivo del rechazo:
                        </strong>

                        <p
                          style={{
                            margin: "0.2rem 0 0 0",
                            fontSize: "0.8rem",
                            fontStyle: "italic",
                            whiteSpace: "pre-wrap",
                            color: "#7f1d1d",
                          }}
                        >
                          {motivoRechazo}
                        </p>
                      </div>
                    )}

                    {isNegado && !motivoRechazo && (
                      <p
                        style={{
                          margin: "0.4rem 0 0 0",
                          fontSize: "0.75rem",
                          color: "#9ca3af",
                          fontStyle: "italic",
                        }}
                      >
                        (Sin motivo registrado)
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showFormularioViewer && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              width: "90%",
              maxWidth: "800px",
              height: "90%",
              borderRadius: "1rem",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #0e7490 0%, #0891b2 100%)",
                color: "white",
                padding: "1rem 1.25rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.2)",
                  }}
                />

                <div>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      opacity: 0.75,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    Detalle de solicitud
                  </div>

                  <div style={{ fontWeight: 700, fontSize: "1rem" }}>
                    {currentFormularioType === "ALMACÉN"
                      ? "Solicitud de Almacén"
                      : currentFormularioType === "INEXISTENCIA"
                      ? "Certificación de Inexistencia"
                      : currentFormularioType === "ANULAR_CDP"
                      ? "Detalle Anulación CDP"
                      : `Formulario ${currentFormularioType}`}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowFormularioViewer(false);
                  setCurrentSolicitud(null);
                }}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  color: "white",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "1.25rem",
                backgroundColor: "#f8fafc",
              }}
            >
              {/* OTRAS_CDP_RP: PDFs adjuntos + PDFs respuesta + campos formulario */}
              {currentFormularioType === "OTRAS_CDP_RP" &&
                currentFormularioData && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

                    {/* PDFs enviados con el formulario */}
                    {currentSolicitud && extractAttachments(currentSolicitud.attachments).length > 0 && (
                      <div style={{ background: "#fff", borderRadius: "0.75rem", padding: "1rem", border: "1px solid #e5e7eb" }}>
                        <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.95rem", fontWeight: 700, color: "#1e3a8a" }}>
                          📤 Documentos adjuntos
                        </h4>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                          {extractAttachments(currentSolicitud.attachments).map((file, i) => {
                            const url = typeof file === "string" ? file : file?.url || file?.path || file?.file_url;
                            const name = typeof file === "object" && file?.name ? file.name : `Archivo ${i + 1}`;
                            if (!url) return null;
                            return (
                              <button key={i} type="button" onClick={() => handleDownload(url, name)}
                                style={{ padding: "0.5rem 1rem", backgroundColor: "#3b82f6", color: "white", borderRadius: "0.375rem", fontSize: "0.875rem", border: "none", cursor: "pointer" }}>
                                📄 {name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* PDFs de respuesta de presupuesto */}
                    {currentSolicitud && extractAttachments(currentSolicitud.response_attachments).length > 0 && (
                      <div style={{ background: "#fff", borderRadius: "0.75rem", padding: "1rem", border: "1px solid #bbf7d0" }}>
                        <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.95rem", fontWeight: 700, color: "#15803d" }}>
                          📥 Documentos de respuesta
                        </h4>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                          {extractAttachments(currentSolicitud.response_attachments).map((file, i) => {
                            const url = typeof file === "string" ? file : file?.url;
                            const name = typeof file === "object" && file?.name ? file.name : `Respuesta ${i + 1}`;
                            if (!url) return null;
                            return (
                              <button key={i} type="button" onClick={() => handleDownload(url, name)}
                                style={{ padding: "0.5rem 1rem", backgroundColor: "#10b981", color: "white", borderRadius: "0.375rem", fontSize: "0.875rem", border: "none", cursor: "pointer" }}>
                                📄 {name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Campos del formulario */}
                    <div style={{ background: "#ffffff", borderRadius: "0.75rem", padding: "1.25rem", border: "1px solid #e5e7eb" }}>
                      <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem", fontWeight: 700, color: "#111827" }}>
                        Información cargada en el formulario
                      </h3>
                      {Object.entries(currentFormularioData)
                        .filter(([, value]) => !isEmptyValue(value))
                        .map(([key, value]) => {
                          const label = formatFieldLabel(key);
                          return (
                            <div key={key} style={{ marginBottom: "0.75rem", paddingBottom: "0.5rem", borderBottom: "1px solid #f1f5f9", wordBreak: "break-word" }}>
                              <strong style={{ color: "#1e3a8a" }}>{label}:</strong>{" "}
                              <span style={{ color: "#374151" }}>
                                {typeof value === "object" ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

              {currentFormularioType === "CDP" && currentFormularioData && (
                <CDPViewer cdpData={currentFormularioData} />
              )}

              {currentFormularioType === "RP" && currentFormularioData && (
                <RPViewer rpData={currentFormularioData} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}